/**
 * Data Review Component
 * Professional, detailed data review with column selection and quality analysis
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
} from '@tanstack/react-table';
import { useDataStore } from '../stores/dataStore';

interface ColumnStats {
  name: string;
  type: 'numeric' | 'date' | 'text';
  missing: number;
  missingPct: number;
  unique: number;
  min?: number;
  max?: number;
  mean?: number;
  median?: number;
  stdDev?: number;
  q1?: number;
  q3?: number;
  outliers: number;
}

interface DataReviewProps {
  onConfirm?: () => void;
}

export function DataReview({ onConfirm }: DataReviewProps) {
  const { rawData, setColumnSelection } = useDataStore();

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [processingIssues, setProcessingIssues] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Calculate detailed column statistics
  const columnStats = useMemo<ColumnStats[]>(() => {
    if (!rawData || !rawData.rows) return [];

    const columns = rawData.columns?.map(c => c.name) || Object.keys(rawData.rows[0]);
    return columns.map(colName => {
      const values = rawData.rows.map((row: any) => row[colName]);
      const nonNull = values.filter(v => v != null && v !== '');
      const missing = values.length - nonNull.length;
      const unique = new Set(nonNull).size;

      let type: 'numeric' | 'date' | 'text' = 'text';
      let stats: Partial<ColumnStats> = {};

      // Detect dates first
      const dateCount = nonNull.filter(v => {
        const str = String(v);
        if (str.includes('-') || str.includes('/')) {
          return !isNaN(new Date(v).getTime());
        }
        return false;
      }).length;

      if (dateCount / nonNull.length > 0.8) {
        type = 'date';
      } else {
        const numericValues = nonNull.map(v => parseFloat(v)).filter(v => !isNaN(v));
        if (numericValues.length / nonNull.length > 0.8) {
          type = 'numeric';

          const sorted = [...numericValues].sort((a, b) => a - b);
          const mean = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
          const variance =
            numericValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
            numericValues.length;
          const stdDev = Math.sqrt(variance);

          const q1 = sorted[Math.floor(sorted.length * 0.25)];
          const q3 = sorted[Math.floor(sorted.length * 0.75)];
          const iqr = q3 - q1;
          const lowerBound = q1 - 1.5 * iqr;
          const upperBound = q3 + 1.5 * iqr;
          const outliers = numericValues.filter(v => v < lowerBound || v > upperBound).length;

          stats = {
            min: Math.min(...numericValues),
            max: Math.max(...numericValues),
            mean,
            median: sorted[Math.floor(sorted.length / 2)],
            stdDev,
            q1,
            q3,
            outliers,
          };
        }
      }

      return {
        name: colName,
        type,
        missing,
        missingPct: (missing / values.length) * 100,
        unique,
        outliers: stats.outliers || 0,
        ...stats,
      };
    });
  }, [rawData]);

  // Auto-suggest columns
  const suggestedDate = useMemo(() => {
    return columnStats.find(
      c =>
        c.type === 'date' ||
        c.name.toLowerCase().includes('date') ||
        c.name.toLowerCase().includes('time')
    )?.name;
  }, [columnStats]);

  const suggestedTarget = useMemo(() => {
    return columnStats.find(
      c =>
        c.type === 'numeric' &&
        c.name !== suggestedDate &&
        !c.name.toLowerCase().includes('id') &&
        !c.name.toLowerCase().includes('index')
    )?.name;
  }, [columnStats, suggestedDate]);

  // Check for data quality issues
  useEffect(() => {
    if (selectedTarget) {
      const targetCol = columnStats.find(c => c.name === selectedTarget);
      setProcessingIssues(
        (targetCol?.missing ?? 0) > 0 || (targetCol?.outliers ?? 0) > 0
      );
    }
  }, [selectedTarget, columnStats]);

  const handleConfirm = () => {
    if (selectedDate && selectedTarget) {
      setColumnSelection({
        dateColumn: selectedDate,
        targetColumn: selectedTarget,
      });
      onConfirm?.();
    }
  };


  const isValid = selectedDate && selectedTarget && selectedDate !== selectedTarget;

  const columnHelper = createColumnHelper<Record<string, unknown>>();

  const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    if (!rawData) return [];

    return columnStats.map(col =>
      columnHelper.accessor(col.name, {
        header: () => (
          <div className="space-y-1">
            <div className="font-medium text-gray-900">{col.name}</div>
            <div className="text-xs text-gray-500">{col.type}</div>
          </div>
        ),
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined || value === '') {
            return <span className="text-gray-400 text-xs">null</span>;
          }
          return <span className="text-sm text-gray-900">{String(value)}</span>;
        },
      })
    );
  }, [rawData, columnStats, columnHelper]);

  const table = useReactTable({
    data: rawData?.rows ?? [],
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-6">
      {/* Main Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-150 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Map Dataset Columns</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Found {rawData?.rows.length.toLocaleString()} rows × {columnStats.length} columns in file
            </p>
          </div>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className={`px-6 py-2.5 rounded-xl font-bold text-xs shadow-sm transition-all duration-200 cursor-pointer ${
              isValid
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow shadow-blue-500/10'
                : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
            }`}
          >
            {isValid ? 'Confirm & Continue →' : 'Map Columns to Proceed'}
          </button>
        </div>

        {/* Column Selection */}
        <div className="p-6 border-b border-gray-150 bg-slate-50/40">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">
                📅 Date/Time Column
              </label>
              <select
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="w-full px-4 py-3 text-sm border border-gray-250 bg-white rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-400 transition-colors shadow-sm cursor-pointer"
              >
                <option value="">Choose date column...</option>
                {columnStats
                  .filter(c => c.type === 'date' || c.name.toLowerCase().includes('date') || c.name.toLowerCase().includes('time'))
                  .map(col => (
                    <option key={col.name} value={col.name}>
                      {col.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">
                📈 Target Value Column
              </label>
              <select
                value={selectedTarget}
                onChange={e => setSelectedTarget(e.target.value)}
                className="w-full px-4 py-3 text-sm border border-gray-250 bg-white rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-400 transition-colors shadow-sm cursor-pointer"
              >
                <option value="">Choose target column...</option>
                {columnStats
                  .filter(c => c.type === 'numeric' && c.name !== selectedDate)
                  .map(col => (
                    <option key={col.name} value={col.name}>
                      {col.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {/* Column Statistics Table */}
        <div className="px-6 py-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Column Statistics</h3>
          <div className="overflow-x-auto max-h-48 overflow-y-auto border border-gray-200 rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                    Column
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                    Missing
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                    Statistics
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {columnStats.map(col => (
                  <tr key={col.name} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">
                      {col.name}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        col.type === 'date' ? 'bg-green-100 text-green-700' :
                        col.type === 'numeric' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {col.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {col.missing > 0 ? (
                        <span className="text-yellow-600">{col.missing}</span>
                      ) : (
                        <span className="text-green-600">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {col.type === 'numeric' && (
                        <div>
                          μ={col.mean?.toFixed(1)}, σ={col.stdDev?.toFixed(1)}
                          {col.outliers > 0 && (
                            <span className="ml-2 text-yellow-600">
                              ({col.outliers} outliers)
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Data Preview Table - separate card for reference */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Data Preview (First 10 rows)</h3>
        </div>
        <div className="px-6 py-4">
          <div className="overflow-x-auto max-h-60 overflow-y-auto border border-gray-200 rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-700"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table
                  .getRowModel()
                  .rows.slice(0, 10)
                  .map(row => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-3 py-1.5 text-xs">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Showing 10 of {rawData?.rows.length.toLocaleString()} rows
          </div>
        </div>
      </div>
    </div>
  );
}
